import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { ThemeContext, THEMES, type ThemeName } from './ThemeContext';
import { useThemeSync } from '@/shared/hooks/useThemeSync';
import { useAuthStore } from '@/features/auth';

const STORAGE_KEY = 'bodywave-theme';
const DEFAULT_THEME: ThemeName = 'flat';
const ANONYMOUS_DEFAULT_THEME: ThemeName = 'cyberpunk'; // Default theme for anonymous users (dark mode)

/**
 * Get initial theme from localStorage or system preference
 */
const getInitialTheme = (): ThemeName => {
  // Check localStorage first
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && isValidTheme(stored)) {
    return stored as ThemeName;
  }

  // For anonymous users (no stored theme), use videogame theme
  return ANONYMOUS_DEFAULT_THEME;
};

/**
 * Validate theme name
 */
const isValidTheme = (theme: string): boolean => {
  return THEMES.some((t) => t.name === theme);
};

/**
 * Apply theme to document
 */
const applyTheme = (theme: ThemeName): void => {
  document.documentElement.setAttribute('data-theme', theme);
};

export interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider Component
 * Manages theme state, localStorage persistence, database sync, and applies theme to document
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<ThemeName>(getInitialTheme);
  const [isLoading, setIsLoading] = useState(false);
  const { fetchTheme, saveTheme } = useThemeSync();
  const { isAuthenticated } = useAuthStore();

  // Fetch theme from database on mount and authentication
  useEffect(() => {
    const loadThemeFromDatabase = async () => {
      if (!isAuthenticated) return;

      setIsLoading(true);
      const dbTheme = await fetchTheme();

      if (dbTheme && dbTheme !== theme) {
        // Database theme takes priority over localStorage
        setThemeState(dbTheme);
        localStorage.setItem(STORAGE_KEY, dbTheme);
        applyTheme(dbTheme);
      }
      setIsLoading(false);
    };

    loadThemeFromDatabase();
    // fetchTheme is now stable (memoized with useCallback in useThemeSync)
    // so including it in deps is safe, but only isAuthenticated is needed
  }, [isAuthenticated, fetchTheme]); // Runs on mount + auth changes

  // Apply theme to document on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Handle theme change
  const setTheme = useCallback(async (newTheme: ThemeName): Promise<void> => {
    if (!isValidTheme(newTheme)) {
      console.error(`Invalid theme: ${newTheme}`);
      return;
    }

    // Update state and localStorage immediately for instant feedback
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
    applyTheme(newTheme);

    // Save to database in background if authenticated
    if (isAuthenticated) {
      const success = await saveTheme(newTheme);
      if (!success) {
        console.warn('Failed to save theme to database, but local state updated');
      }
    }
    // saveTheme is intentionally omitted from deps to prevent infinite loop
    // (it's recreated on every render, but doesn't depend on reactive values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    const currentThemeMetadata = THEMES.find((t) => t.name === theme) || THEMES[2]; // Default to flat

    return {
      theme,
      setTheme,
      themes: THEMES,
      currentThemeMetadata,
      isLoading,
    };
  }, [theme, isLoading, setTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
